/**
 * useVoice — Browser-native voice input/output hooks for Felix.
 *
 * Speech-to-Text: Uses the Web Speech API (SpeechRecognition).
 *   - Chrome/Edge only; gracefully reports unsupported in other browsers.
 *   - Returns interim + final transcripts, listening state, and controls.
 *
 * Text-to-Speech: Uses the Web Speech API (SpeechSynthesis).
 *   - All modern browsers supported.
 *   - Queues utterances, exposes speaking state and stop control.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { toSpeakable } from "@/lib/toSpeakable";

// ---------------------------------------------------------------------------
// Feature detection
// ---------------------------------------------------------------------------

export function isSpeechRecognitionSupported(): boolean {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

export function isSpeechSynthesisSupported(): boolean {
  return "speechSynthesis" in window;
}

// ---------------------------------------------------------------------------
// useSpeechRecognition — voice input (mic → text)
// ---------------------------------------------------------------------------

interface UseSpeechRecognitionReturn {
  /** Whether the browser supports speech recognition */
  supported: boolean;
  /** Whether the mic is currently listening */
  listening: boolean;
  /** Current transcript (interim + final combined) */
  transcript: string;
  /** Last error message (cleared on next start) */
  error: string | null;
  /** Start listening */
  start: () => void;
  /** Stop listening (finalizes current speech) */
  stop: () => void;
  /** Clear the transcript buffer */
  clear: () => void;
}

export function useSpeechRecognition(): UseSpeechRecognitionReturn {
  const supported = isSpeechRecognitionSupported();
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef("");

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
    };
  }, []);

  const start = useCallback(() => {
    if (!supported) return;

    // If already listening, stop first
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.maxAlternatives = 1;

    finalTranscriptRef.current = "";
    setTranscript("");
    setError(null);

    recognition.onstart = () => {
      setListening(true);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscriptRef.current += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      setTranscript(finalTranscriptRef.current + interim);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // "aborted" is expected when we call stop/abort ourselves
      if (event.error !== "aborted") {
        console.warn("Speech recognition error:", event.error);
        const messages: Record<string, string> = {
          "not-allowed": "Microphone access denied. Please allow microphone permission in your browser settings.",
          "no-speech": "No speech detected. Try speaking louder or closer to the microphone.",
          "audio-capture": "No microphone found. Please check your audio input device.",
          "network": "Network error during speech recognition. Please check your connection.",
        };
        setError(messages[event.error] || `Speech recognition error: ${event.error}`);
      }
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (err) {
      console.warn("Speech recognition failed to start:", err);
      setListening(false);
      recognitionRef.current = null;
    }
  }, [supported]);

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  const clear = useCallback(() => {
    finalTranscriptRef.current = "";
    setTranscript("");
  }, []);

  return { supported, listening, transcript, error, start, stop, clear };
}

// ---------------------------------------------------------------------------
// useSpeechSynthesis — voice output (text → speech)
// ---------------------------------------------------------------------------

interface UseSpeechSynthesisReturn {
  /** Whether the browser supports speech synthesis */
  supported: boolean;
  /** Whether Felix is currently speaking */
  speaking: boolean;
  /** Speak the given text */
  speak: (text: string) => void;
  /** Stop speaking immediately */
  cancel: () => void;
}

export function useSpeechSynthesis(): UseSpeechSynthesisReturn {
  const supported = isSpeechSynthesisSupported();
  const [speaking, setSpeaking] = useState(false);

  // Cancel on unmount
  useEffect(() => {
    return () => {
      if (supported && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [supported]);

  const speak = useCallback(
    (text: string) => {
      if (!supported || !text.trim()) return;

      // Cancel any in-progress speech
      window.speechSynthesis.cancel();

      // Strip markdown + machine/UI-directive tokens for cleaner speech (#585).
      // Uses the shared toSpeakable() so the "read aloud" surface matches the
      // felix-agent voice path — never narrates "star star" or recites
      // directive tokens, and also handles underscore emphasis, code fences,
      // numbered lists, and whitespace the old inline strip missed.
      const clean = toSpeakable(text);
      if (!clean) return;

      const utterance = new SpeechSynthesisUtterance(clean);
      utterance.rate = 1.05; // slightly faster than default for natural feel
      utterance.pitch = 1.0;

      // Try to pick a good English voice
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find(
        (v) =>
          v.lang.startsWith("en") &&
          (v.name.includes("Google") || v.name.includes("Samantha") || v.name.includes("Daniel")),
      );
      if (preferred) {
        utterance.voice = preferred;
      }

      utterance.onstart = () => setSpeaking(true);
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);

      window.speechSynthesis.speak(utterance);
    },
    [supported],
  );

  const cancel = useCallback(() => {
    if (supported) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  }, [supported]);

  return { supported, speaking, speak, cancel };
}
